import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Editor } from '@digitalsamba/tldraw';
import type {
  ContentBoardAvailablePanelTool,
  ContentBoardColor,
  ContentBoardFill,
} from 'features/content-whiteboard/types';
import {
  panelColorChanged,
  panelFillChanged,
  panelOpacityChanged,
  panelToolChanged,
  selectContentWhiteboardPanelColor,
  selectContentWhiteboardPanelFill,
  selectContentWhiteboardPanelOpacity,
  selectContentWhiteboardPanelTool,
} from 'features/content-whiteboard/contentWhiteboardSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export type ContentWhiteboardContextValue = {
  currentEditor: Editor | null;
  setCurrentEditor: (editor: Editor | null) => void;
  currentSlideIndex: number;
  setCurrentSlideIndex: (index: number) => void;
  currentColor: ContentBoardColor;
  currentTool: ContentBoardAvailablePanelTool;
  currentOpacity: number;
  currentFill: ContentBoardFill;
  setCurrentTool: (tool: ContentBoardAvailablePanelTool) => void;
  setCurrentColor: (color: ContentBoardColor) => void;
  setCurrentOpacity: (opacity: number) => void;
  setCurrentFill: (fill: ContentBoardFill) => void;
};

const ContentWhiteboardContext = createContext<ContentWhiteboardContextValue | null>(null);

export const ContentWhiteboardProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const dispatch = useAppDispatch();

  const [currentEditor, setCurrentEditor] = useState<Editor | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const currentTool = useAppSelector(selectContentWhiteboardPanelTool);
  const currentColor = useAppSelector(selectContentWhiteboardPanelColor);
  const currentOpacity = useAppSelector(selectContentWhiteboardPanelOpacity);
  const currentFill = useAppSelector(selectContentWhiteboardPanelFill);

  const setCurrentTool = useCallback(
    (tool: ContentBoardAvailablePanelTool) => {
      dispatch(panelToolChanged(tool));
    },
    [dispatch]
  );

  const setCurrentColor = useCallback(
    (color: ContentBoardColor) => {
      dispatch(panelColorChanged(color));
    },
    [dispatch]
  );

  const setCurrentOpacity = useCallback(
    (opacity: number) => {
      dispatch(panelOpacityChanged(opacity));
    },
    [dispatch]
  );

  const setCurrentFill = useCallback(
    (fill: ContentBoardFill) => {
      dispatch(panelFillChanged(fill));
    },
    [dispatch]
  );

  const value = useMemo<ContentWhiteboardContextValue>(
    () => ({
      currentEditor,
      setCurrentEditor,
      currentSlideIndex,
      setCurrentSlideIndex,
      currentTool,
      setCurrentTool,
      currentColor,
      setCurrentColor,
      currentOpacity,
      setCurrentOpacity,
      currentFill,
      setCurrentFill,
    }),
    [
      currentEditor,
      currentSlideIndex,
      currentColor,
      currentTool,
      currentOpacity,
      currentFill,
      setCurrentTool,
      setCurrentColor,
      setCurrentOpacity,
      setCurrentFill,
    ]
  );

  return (
    <ContentWhiteboardContext.Provider value={value}>{children}</ContentWhiteboardContext.Provider>
  );
};

export const useContentWhiteboardContext = (): ContentWhiteboardContextValue => {
  const context = useContext(ContentWhiteboardContext);
  if (!context) {
    throw new Error('useContentWhiteboard must be used within ContentWhiteboardProvider');
  }
  return context;
};
